/* sb-dc-retry.js — retry the reads that had no business failing, and tell the
   customer what the cart actually said. 2026-09-05.

   ── 1. products/<handle>.js has no retry ───────────────────────────────────
   The cart module reads the catalogue with a bare req(u, {}, 15000) —
   fetchVariants (sb-dc-aug.js:5157), fetchAllVariants (:5065) and the add-on
   lookup (:5889) all do. One 429, one 5xx, one slow response and a perfectly
   good order stops with

       "Orders are coming in all at once — press it again in about 30 seconds"

   which is the code asking the customer to do the retry it should have done
   itself. Same read, same failure, three symptoms: that message, "Could not
   reach the store", and — because the add-on lookup uses the same endpoint —
   "Could not add the extra products", which blocks the BANNER over a stand.

   Retrying here fixes all three at once, which is why the add-on path needs no
   change of its own. The alternative (drop the add-on and add the banner
   anyway) was rejected: a stand the customer ticked disappearing without a
   word is worse than the error.

   Rule and timing are lifted from the one retry loop the codebase already has,
   the print upload at sb-dc-aug.js:4959 — 429 / 5xx / no response, 1.5s then
   3s, two extra tries.

   ── 2. cart/add.js retries on 429 ONLY ─────────────────────────────────────
   By the time the cart is called the print file has been rendered, encoded and
   uploaded. Losing that to one rate-limited request costs the customer the
   whole wait again.

   But /cart/add.js is not idempotent. A 5xx or a dropped connection can mean
   the line WAS added and only the answer went missing; re-sending would put
   the same banner in the cart twice. 429 is refused before Shopify processes
   anything, so it is the only status that is safe to repeat. The others are
   left alone deliberately.

   ── 3. the failure reason shown is the wrong one ───────────────────────────
   sb-dc-aug.js:5579 fills the detail line from window.__dcErrors[last] — the
   GLOBAL error ring. An add-on lookup error arriving later is displayed as the
   reason the cart failed. And what Shopify actually said ("All 3 Custom Vinyl
   Banner are in your cart") is carried on e.body and never shown. We keep the
   real description and swap it in.

   ── load order ─────────────────────────────────────────────────────────────
   FIRST among the fetch wrappers, so this one sits closest to the real fetch
   and the retrying finishes before varfix, sizeguard, cartprops, cartlog and
   upload ever see a result. They then log one outcome instead of one per
   attempt. sizeguard's synthetic 422 is produced OUTSIDE this wrapper, so a
   size-mismatch block is never retried and never re-labelled.

   Only string URLs with a string (or absent) body are retried — a Request
   object or a stream body can be read once, and re-sending it is not something
   this file is willing to guess at. Everything else passes straight through.

   Diagnostics: window.__dcRetryLog (attempts), window.__dcCartReason (the last
   thing the cart said). */
(function () {
  'use strict';

  var realFetch = window.fetch.bind(window);
  var LOG = [];
  window.__dcRetryLog = LOG;

  var PRODUCT = /\/products\/[^/?#]+\.js(\?|$)/;
  var CART_ADD = /\/cart\/add(\.js)?(\?|$)/;

  var TRIES = 2;            // 첫 시도 뒤 추가 시도 횟수
  var WAIT = 1500;          // 1.5s → 3s (sb-dc-aug.js:4976 과 같다)

  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function log(row) { row.at = new Date().toISOString(); LOG.push(row); if (LOG.length > 50) LOG.shift(); }

  /* ---------------------------------------------------------------- 실패 사유 */
  var reason = null, reasonAt = 0;

  function keepReason(res) {
    try {
      res.clone().json().then(function (j) {
        var d = j && (j.description || j.message);
        if (d) { reason = String(d); reasonAt = Date.now(); window.__dcCartReason = reason; }
      }).catch(function () {});
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- fetch */
  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var isProduct = PRODUCT.test(url);
    var isCart = CART_ADD.test(url);
    if (!isProduct && !isCart) return realFetch.apply(this, arguments);

    // 다시 보낼 수 있는 모양인지 — 아니면 손대지 않는다
    var body = init && init.body;
    if (typeof input !== 'string' || (body != null && typeof body !== 'string')) {
      return realFetch.apply(this, arguments);
    }

    var signal = init && init.signal;
    var self = this, args = arguments;

    /* 담기는 429 만. 500대·응답 없음은 이미 담겼을 수 있어 다시 보내면
       같은 상품이 두 줄 담긴다. */
    function again(status) { return isCart ? status === 429 : (status === 429 || status >= 500); }

    function attempt(left, delay) {
      return realFetch.apply(self, args).then(function (res) {
        if (res.ok || left <= 0 || !again(res.status)) return res;
        /* req() 의 AbortController 는 전체에 한 번 걸려 있고 시도마다 새로
           걸리지 않는다. 이미 끊긴 뒤라면 다시 보내도 즉시 실패한다. */
        if (signal && signal.aborted) return res;
        log({ url: url, status: res.status, left: left });
        return wait(delay).then(function () { return attempt(left - 1, delay * 2); });
      }, function (e) {
        if (isCart || left <= 0) throw e;                       // 담기는 응답 없음으로 재시도하지 않는다
        if (e && e.name === 'AbortError') throw e;              // 시간 초과 — 같은 signal 로는 못 보낸다
        if (signal && signal.aborted) throw e;
        log({ url: url, error: String((e && e.message) || e), left: left });
        return wait(delay).then(function () { return attempt(left - 1, delay * 2); });
      });
    }

    return attempt(TRIES, WAIT).then(function (res) {
      if (isCart && !res.ok) keepReason(res);
      return res;
    });
  };

  /* ---------------------------------------------------------------- 사유 교체
     담기가 실패하면 sb-dc-aug.js 가 .sbdc-cart-err 을 승인 창 맨 위에 넣는다.
     그 상세 줄만 진짜 사유로 바꾼다. 머리글과 나머지 오류는 건드리지 않는다. */
  var HEAD = 'Could not add to the cart';

  function swap() {
    var el = document.querySelector('.sbdc-cart-err');
    if (!el || el.getAttribute('data-sbdc-reason')) return;
    if (!reason || Date.now() - reasonAt > 15000) return;
    var b = el.querySelector('b'), em = el.querySelector('em');
    if (!b || !em || (b.textContent || '').indexOf(HEAD) < 0) return;
    el.setAttribute('data-sbdc-reason', '1');                   // 내 수정에 또 반응하지 않도록
    em.textContent = reason + ' — Please try again shortly.';
  }

  function start() {
    try {
      var mo = new MutationObserver(function () { try { swap(); } catch (e) {} });
      mo.observe(document.body, { childList: true, subtree: true });
      window.addEventListener('pagehide', function () { mo.disconnect(); });
    } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* 담기를 새로 누르면 지난 사유는 버린다. sizeguard 가 막은 경우처럼 이
     래퍼까지 오지 않는 실패도 있어서, 오래된 사유가 남아 다음 오류에 붙는
     것을 막는다. (__dcAddToCart 를 기다리는 방식은 varfix 와 같다) */
  var t = setInterval(function () {
    if (typeof window.__dcAddToCart !== 'function') return;
    clearInterval(t);
    var orig = window.__dcAddToCart;
    window.__dcAddToCart = function () {
      reason = null; reasonAt = 0; window.__dcCartReason = null;
      return orig.apply(this, arguments);
    };
  }, 400);
  setTimeout(function () { clearInterval(t); }, 60000);
})();
